import { TFunction } from 'i18next';
import { toLocaleDateTime } from 'utils/datetime';
import {
  getBranchRulesMarkdownReport,
  getBranchRulesRepositoryProblemText,
  GitHubBranchResult,
  GitHubBranchRule,
  GitHubBranchRuleResult,
  GitHubBranchRulesConfig,
  GitHubBranchRulesEvidence,
  GitHubBranchRulesRepository,
  readBranchRulesEvidence,
} from './IntegrationConfig_GitHub_BranchRules';

//
// Branch rules (PLAN-FBL-003): a branch name, and whether its CODEOWNERS file must be free of errors (CIS 1.1.6)
//
export class CodeOwnersRule extends GitHubBranchRule {
  //the file must parse without errors
  requireNoErrors: boolean = false;

  clone(): this {
    const rule = new CodeOwnersRule();
    rule.branch = this.branch;
    rule.requireNoErrors = this.requireNoErrors;

    return rule as this;
  }

  isEqual(other: this): boolean {
    return this.branch.trim() === other.branch.trim() && this.requireNoErrors === other.requireNoErrors;
  }
}

//
// Config class: the API's GitHub_CodeOwnersConfig
//
export class IntegrationConfig_GitHub_CodeOwners extends GitHubBranchRulesConfig<CodeOwnersRule> {
  defaultRules: CodeOwnersRule[] = [new CodeOwnersRule()];

  protected newRule(): CodeOwnersRule {
    return new CodeOwnersRule();
  }

  static from(stored: unknown): IntegrationConfig_GitHub_CodeOwners {
    const config = new IntegrationConfig_GitHub_CodeOwners();
    config.readBranchRules((stored ?? {}) as Parameters<typeof config.readBranchRules>[0]);

    return config;
  }
}

//The problem codes of the API's GitHub_CodeOwnersHandler, stable in the evidence
export const getCodeOwnersProblemText = (code: string, t: TFunction<string[]>): string => {
  switch (code) {
    case 'CodeOwnersMissing':
      return t('integrations:GitHub_CodeOwners.Problems.CodeOwnersMissing');
    case 'CodeOwnersEmpty':
      return t('integrations:GitHub_CodeOwners.Problems.CodeOwnersEmpty');
    case 'CodeOwnersErrors':
      return t('integrations:GitHub_CodeOwners.Problems.CodeOwnersErrors');
    case 'BranchNotFound':
      return t('integrations:GitHub_CodeOwners.Problems.BranchNotFound');
    //read through the code app, not the main app the shared text names
    case 'RepositoryNotFound':
      return t('integrations:GitHub_CodeOwners.Problems.RepositoryNotFound');
    default:
      return getBranchRulesRepositoryProblemText(code, t);
  }
};

//One error GitHub found in the file; the kind is GitHub's own English text
export class CodeOwnersError {
  line: number = 0;

  kind: string = '';
}

//
// Evidence classes: repository > rule > branch
//
export class CodeOwnersBranchResult extends GitHubBranchResult {
  //where the file is; null when the branch has none
  path: string | null = null;

  sizeBytes: number = 0;

  errorCount: number = 0;

  //the first errors only
  errors: CodeOwnersError[] = [];

  //the file could not be looked for: the branch does not exist
  get isUnknown(): boolean {
    return this.problems.includes('BranchNotFound');
  }

  getFoundText(t: TFunction<string[]>): string {
    if (this.isUnknown) return '-';
    if (!this.path) return t('integrations:GitHub_CodeOwners.Result.Missing');
    if (this.errorCount > 0) {
      return t('integrations:GitHub_CodeOwners.Result.FoundWithErrors', { path: this.path, count: this.errorCount });
    }

    return this.path;
  }
}

//One rule as it was judged, with the requirement it was judged by: the screen rebuilds the rules from it
export class CodeOwnersRuleResult extends GitHubBranchRuleResult<CodeOwnersBranchResult> {
  requireNoErrors: boolean = false;
}

export type GitHub_CodeOwnersRepository = GitHubBranchRulesRepository<CodeOwnersRuleResult>;

export class GitHub_CodeOwnersEvidence extends GitHubBranchRulesEvidence<CodeOwnersRuleResult> {
  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const typedT = t as TFunction<string[]>;

    return getBranchRulesMarkdownReport<CodeOwnersBranchResult>(
      this,
      t('integrations:GitHub_CodeOwners.Report.SummaryHeading', {
        organization: this.organization,
        date: toLocaleDateTime(this.evaluatedAtUtc),
      }),
      (code) => getCodeOwnersProblemText(code, typedT),
      (branch) => branch.getFoundText(typedT),
      typedT,
    );
  }
}

//The nested shape only; a result from before it shows as "test again"
export const parse_Evidence_GitHub_CodeOwners = (raw: string | undefined): GitHub_CodeOwnersEvidence | undefined =>
  readBranchRulesEvidence(
    new GitHub_CodeOwnersEvidence(),
    raw,
    (item) => {
      const branch = new CodeOwnersBranchResult();
      branch.path = typeof item.path === 'string' ? item.path : null;
      branch.sizeBytes = typeof item.sizeBytes === 'number' ? item.sizeBytes : 0;
      branch.errorCount = typeof item.errorCount === 'number' ? item.errorCount : 0;
      branch.errors = Array.isArray(item.errors)
        ? item.errors.map((entry: Record<string, unknown>) => {
            const error = new CodeOwnersError();
            error.line = typeof entry.line === 'number' ? entry.line : 0;
            error.kind = typeof entry.kind === 'string' ? entry.kind : '';

            return error;
          })
        : [];

      return branch;
    },
    (item) => {
      const rule = new CodeOwnersRuleResult();
      rule.requireNoErrors = item.requireNoErrors === true;

      return rule;
    },
  );
